/**
 * SubtitleModal — Full-page modal for subtitle management.
 *
 * Opened by the CC toolbar button on media_page content type.
 * Shows:
 *  - List of existing subtitles with links to SubtitleView
 *  - "Add a subtitle" form: pick a language, then either generate one from the
 *    media or create an empty one to write by hand
 */
import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Modal,
  ModalContent,
  ModalActions,
  Header,
  Button,
  Confirm,
  Dropdown,
  Form,
  Checkbox,
  Message,
} from 'semantic-ui-react';
import request from 'superagent';
import config from '@plone/volto/registry';
import { getContent } from '@plone/volto/actions/content/content';
import deleteSVG from '@plone/volto/icons/delete.svg';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Api from '@plone/volto/helpers/Api/Api';
import { buildSubtitleLabel } from 'volto-base-addon/helpers/subtitleLabel';
import {
  getLanguageOptions,
  getLanguageEndonym,
  getLanguageName,
} from 'volto-base-addon/helpers/languageOptions';
import { formatFileSize } from 'volto-base-addon/helpers/fileSize';
import { fetchEnrichedContent } from 'volto-base-addon/components/manage/Blocks/VideoJS/useContentFreshness';
import { setMediaPageEnriched } from 'volto-base-addon/reducers/mediaPageEnriched';
import { tokenOf } from 'volto-base-addon/helpers/vocabulary';
import { MEDIA_PAGE_TRANSCRIPTION_ROUTE } from 'volto-base-addon/constants';
import { subtitleGenerationStarted } from 'volto-base-addon/reducers/subtitleGeneration';
import './SubtitleModal.css';

const messages = defineMessages({
  title: {
    id: 'SubtitleToolbar.panelTitle',
    defaultMessage: 'Subtitles',
  },
  existingSection: {
    id: 'SubtitleToolbar.existingSection',
    defaultMessage: 'Existing subtitles',
  },
  noSubtitles: {
    id: 'SubtitleToolbar.noSubtitles',
    defaultMessage: 'No subtitles yet.',
  },
  addSection: {
    id: 'SubtitleToolbar.addSection',
    defaultMessage: 'Add a subtitle',
  },
  generateOptions: {
    id: 'SubtitleToolbar.generateOptions',
    defaultMessage: 'Generation options',
  },
  language: {
    id: 'SubtitleToolbar.language',
    defaultMessage: 'Language',
  },
  speakerDiarization: {
    id: 'SubtitleToolbar.speakerDiarization',
    defaultMessage: 'Speaker diarization',
  },
  includeFillerWords: {
    id: 'SubtitleToolbar.includeFillerWords',
    defaultMessage: 'Include filler words',
  },
  translateToEnglish: {
    id: 'SubtitleToolbar.translateToEnglish',
    defaultMessage: 'Translate to English',
  },
  generate: {
    id: 'SubtitleToolbar.generate',
    defaultMessage: 'Generate from media',
  },
  addEmpty: {
    id: 'SubtitleToolbar.addEmpty',
    defaultMessage: 'Add empty',
  },
  selectLanguage: {
    id: 'SubtitleToolbar.selectLanguage',
    defaultMessage: 'Select language…',
  },
  generationStarted: {
    id: 'SubtitleToolbar.generationStarted',
    defaultMessage: 'Subtitle generation started. You can close this dialog.',
  },
  generationFailed: {
    id: 'SubtitleToolbar.generationFailed',
    defaultMessage: 'Failed to start generation. Please try again.',
  },
  noAudio: {
    id: 'SubtitleToolbar.noAudio',
    defaultMessage:
      'This media page has no audio track to transcribe yet. Wait until processing has finished, then try again.',
  },
  addEmptyFailed: {
    id: 'SubtitleToolbar.addEmptyFailed',
    defaultMessage: 'Could not create the subtitle. Please try again.',
  },
  taskTranscribe: {
    id: 'SubtitleToolbar.taskTranscribe',
    defaultMessage: 'Transcription',
  },
  taskTranslate: {
    id: 'SubtitleToolbar.taskTranslate',
    defaultMessage: 'Translation',
  },
  stateGenerating: {
    id: 'SubtitleToolbar.stateGenerating',
    defaultMessage: 'Being generated…',
  },
  stateFailed: {
    id: 'SubtitleToolbar.stateFailed',
    defaultMessage: 'Generation failed',
  },
  stateEmpty: {
    id: 'SubtitleToolbar.stateEmpty',
    defaultMessage: 'No content yet',
  },
  deleteSubtitle: {
    id: 'SubtitleToolbar.deleteSubtitle',
    defaultMessage: 'Delete subtitle {name}',
  },
  confirmDeleteHeader: {
    id: 'SubtitleToolbar.confirmDeleteHeader',
    defaultMessage: 'Delete subtitle',
  },
  confirmDeleteBody: {
    id: 'SubtitleToolbar.confirmDeleteBody',
    defaultMessage: 'Delete the subtitle “{name}”? This cannot be undone.',
  },
  confirmDeleteOk: {
    id: 'SubtitleToolbar.confirmDeleteOk',
    defaultMessage: 'Delete',
  },
  confirmDeleteCancel: {
    id: 'SubtitleToolbar.confirmDeleteCancel',
    defaultMessage: 'Cancel',
  },
  deleteFailed: {
    id: 'SubtitleToolbar.deleteFailed',
    defaultMessage: 'Could not delete the subtitle. Please try again.',
  },
});

// media_output_workflow, minus `completed` which is the ordinary case.
const STATE_LABELS = {
  in_progress: messages.stateGenerating,
  failed: messages.stateFailed,
  inactive: messages.stateEmpty,
};

const TASK_LABELS = {
  transcribe: messages.taskTranscribe,
  translate: messages.taskTranslate,
};

const SubtitleModal = ({ open, onClose, content }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const enrichedContent = useSelector(
    (state) => state.mediaPageEnriched?.content,
  );
  const [language, setLanguage] = useState('');
  const [diarization, setDiarization] = useState(false);
  const [fillers, setFillers] = useState(false);
  const [translate, setTranslate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const token = useSelector((state) => state.userSession?.token);

  // Every subtitle, not just the completed ones: a generation started from
  // this very dialog has to stay visible while it runs.
  const subtitles = React.useMemo(() => {
    const items = enrichedContent?.items;
    if (!Array.isArray(items)) return [];
    return items.filter((item) => item['@type'] === 'subtitle' && item['@id']);
  }, [enrichedContent]);

  const parentUrl = content ? flattenToAppURL(content['@id']) : '';

  const languageOptions = getLanguageOptions(intl.locale).map((opt) => ({
    key: opt.code,
    value: opt.code,
    text: opt.name,
  }));

  const handleGenerate = async () => {
    if (!language) return;
    setLoading(true);
    setError(null);
    try {
      const mediaPageUid = content.UID;
      const subtitleData = {
        transcription: true,
        diarization,
        fillers,
        language,
        translate: translate && language !== 'en',
        useInitialPromptFi: fillers && language === 'fi',
        useInitialPromptEn: fillers && language === 'en',
      };
      await request
        .post(
          `${config.settings.publicPath}/${MEDIA_PAGE_TRANSCRIPTION_ROUTE}/${mediaPageUid}`,
        )
        .send({
          operationsFinal: {
            subtitles: subtitleData,
          },
        });
      // Signal the processing hooks to start polling for the new subtitle.
      dispatch(subtitleGenerationStarted(mediaPageUid));
      dispatch(getContent(parentUrl));
      setGenerated(true);
    } catch (e) {
      // 409 is the backend refusing for a reason the user can act on, so it is
      // shown, not logged. Anything else is a raw superagent string — never
      // localized, often meaningless to an editor — so it is logged instead.
      if (e?.status === 409) {
        setError(intl.formatMessage(messages.noAudio));
      } else {
        // eslint-disable-next-line no-console
        console.error('Subtitle generation failed:', e);
        setError(intl.formatMessage(messages.generationFailed));
      }
    } finally {
      setLoading(false);
    }
  };

  // Creates the child here rather than sending the user to the raw /add form,
  // which offers Internals to someone who only wants to paste a VTT.
  const handleAddEmpty = async () => {
    if (!language) return;
    setAdding(true);
    setError(null);
    try {
      const api = new Api();
      const created = await api.post(parentUrl, {
        data: {
          '@type': 'subtitle',
          title: getLanguageEndonym(language),
          subtitle_language: language,
        },
      });
      // Load it before routing: the edit route picks its component from the
      // content type in the store, which is still this media page's.
      const path = flattenToAppURL(created['@id']);
      await dispatch(getContent(path));
      onClose();
      history.push(`${path}/edit`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Adding an empty subtitle failed:', e);
      setError(intl.formatMessage(messages.addEmptyFailed));
    } finally {
      setAdding(false);
    }
  };

  // The IObjectRemovedEvent subscriber rebuilds the manifest; this only has to
  // put the list back in step with the server.
  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      const api = new Api();
      await api.del(flattenToAppURL(pendingDelete['@id']), {});
      const refreshed = await fetchEnrichedContent(parentUrl, token, api);
      if (refreshed) dispatch(setMediaPageEnriched(refreshed));
      dispatch(getContent(parentUrl));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Deleting a subtitle failed:', e);
      setError(intl.formatMessage(messages.deleteFailed));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleClose = () => {
    setGenerated(false);
    setError(null);
    setPendingDelete(null);
    onClose();
  };

  const busy = loading || adding;

  return (
    <Modal
      className="subtitle-modal"
      open={open}
      onClose={handleClose}
      closeIcon
      size="small"
      aria-labelledby="subtitle-modal-title"
    >
      <Header id="subtitle-modal-title">
        {intl.formatMessage(messages.title)}
      </Header>
      <ModalContent>
        <h3 className="subtitle-modal-section-title">
          {intl.formatMessage(messages.existingSection)}
        </h3>
        {subtitles.length === 0 ? (
          <p className="subtitle-modal-empty">
            {intl.formatMessage(messages.noSubtitles)}
          </p>
        ) : (
          <ul className="subtitle-modal-list">
            {subtitles.map((item) => (
              <SubtitleListItem
                key={item['@id']}
                item={item}
                onNavigate={handleClose}
                onDelete={() => setPendingDelete(item)}
              />
            ))}
          </ul>
        )}

        <hr className="subtitle-modal-divider" />

        <h3 className="subtitle-modal-section-title">
          {intl.formatMessage(messages.addSection)}
        </h3>
        {generated ? (
          <Message positive role="alert">
            <Message.Content>
              {intl.formatMessage(messages.generationStarted)}
            </Message.Content>
          </Message>
        ) : (
          <Form className="subtitle-modal-generate">
            <Form.Field>
              <label htmlFor="subtitle-modal-language">
                {intl.formatMessage(messages.language)}
              </label>
              <Dropdown
                id="subtitle-modal-language"
                selection
                search
                options={languageOptions}
                value={language}
                onChange={(e, { value }) => setLanguage(value)}
                placeholder={intl.formatMessage(messages.selectLanguage)}
                fluid
              />
            </Form.Field>
            <fieldset className="subtitle-modal-generate-options">
              <legend>{intl.formatMessage(messages.generateOptions)}</legend>
              <Form.Field>
                <Checkbox
                  label={intl.formatMessage(messages.speakerDiarization)}
                  checked={diarization}
                  onChange={(e, { checked }) => setDiarization(checked)}
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label={intl.formatMessage(messages.includeFillerWords)}
                  checked={fillers}
                  onChange={(e, { checked }) => setFillers(checked)}
                />
              </Form.Field>
              {language && language !== 'en' && (
                <Form.Field>
                  <Checkbox
                    label={intl.formatMessage(messages.translateToEnglish)}
                    checked={translate}
                    onChange={(e, { checked }) => setTranslate(checked)}
                  />
                </Form.Field>
              )}
            </fieldset>
          </Form>
        )}
        {error ? (
          <Message negative role="alert">
            <Message.Content>{error}</Message.Content>
          </Message>
        ) : null}
      </ModalContent>
      <ModalActions>
        {generated ? (
          <Button onClick={handleClose}>OK</Button>
        ) : (
          <>
            <Button
              className="subtitle-modal-add-empty"
              onClick={handleAddEmpty}
              disabled={!language || busy}
              loading={adding}
            >
              {intl.formatMessage(messages.addEmpty)}
            </Button>
            <Button
              primary
              onClick={handleGenerate}
              disabled={!language || busy}
              loading={loading}
            >
              {intl.formatMessage(messages.generate)}
            </Button>
          </>
        )}
      </ModalActions>
      <Confirm
        className="subtitle-delete-confirm"
        open={!!pendingDelete}
        header={intl.formatMessage(messages.confirmDeleteHeader)}
        content={intl.formatMessage(messages.confirmDeleteBody, {
          name: buildSubtitleLabel(pendingDelete || {}),
        })}
        confirmButton={{
          content: intl.formatMessage(messages.confirmDeleteOk),
          negative: true,
          loading: deleting,
          disabled: deleting,
        }}
        cancelButton={intl.formatMessage(messages.confirmDeleteCancel)}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
      />
    </Modal>
  );
};

/**
 * The label is the subtitle's own title; the secondary line carries what the
 * title cannot — task and size, or the state of anything not yet complete.
 */
const SubtitleListItem = ({ item, onNavigate, onDelete }) => {
  const intl = useIntl();
  const label = buildSubtitleLabel(item);
  const url = flattenToAppURL(item['@id']);
  const state = item.output_state;
  const complete = state === 'completed';
  // Only a generation in flight has nothing to open yet; an empty or failed
  // one is exactly what a user wants to click through to, and to delete.
  const openable = state !== 'in_progress';

  const details = [];
  if (!complete) {
    // Only a state we recognise. A site whose upgrade step has not run serves
    // no output_state at all, and claiming every subtitle is being generated
    // would be worse than saying nothing.
    if (STATE_LABELS[state]) {
      details.push(intl.formatMessage(STATE_LABELS[state]));
    }
  } else {
    const task = TASK_LABELS[tokenOf(item.subtitle_task)];
    if (task) details.push(intl.formatMessage(task));
    if (item.file_size) details.push(formatFileSize(item.file_size));
  }
  // Last resort, so two same-named subtitles still differ; rows are kept even
  // by a min-height, not by this.
  if (details.length === 0) {
    const code = tokenOf(item.subtitle_language);
    if (code) details.push(getLanguageName(code, intl.locale));
  }

  const body = (
    <>
      <span className="subtitle-modal-item-label">{label}</span>
      {details.length > 0 && (
        <span className="subtitle-modal-item-details">
          {details.join(' · ')}
        </span>
      )}
    </>
  );

  return (
    <li className="subtitle-modal-item">
      {openable ? (
        <Link
          to={url}
          className="subtitle-modal-item-link"
          onClick={onNavigate}
        >
          {body}
        </Link>
      ) : (
        <span className="subtitle-modal-item-link is-pending">{body}</span>
      )}
      <button
        type="button"
        className="subtitle-modal-item-delete"
        onClick={onDelete}
        // Camunda is still writing to a generation in flight; removing it now
        // fails later, far from here.
        disabled={!openable}
        aria-label={intl.formatMessage(messages.deleteSubtitle, {
          name: label,
        })}
      >
        <Icon name={deleteSVG} size="24px" />
      </button>
    </li>
  );
};

export default SubtitleModal;
